import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import SEO, { SEO_CONFIG } from '@/components/SEO';

const RefundPage = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-purple-50 to-pink-50 py-12">
      <SEO {...SEO_CONFIG.refund} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <RefreshCw className="h-12 w-12 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-center">
              {isEnglish ? 'Refund & Cancellation Policy' : 'İade ve İptal Politikası'}
            </h1>
          </CardHeader>
          <CardContent className="prose max-w-none">
            {isEnglish ? (
              <div className="space-y-6 text-gray-700">
                <div className="bg-blue-50 p-4 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                  <p className="text-blue-800">
                    Services offered through Pilatika are digital content and live class services.
                  </p>
                </div>

                <h2 className="text-xl font-semibold text-gray-900">Live Classes</h2>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-green-800">
                      <strong>Refund possible:</strong> Cancellations made more than 24 hours before the class start time.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800">
                      <strong>No refund:</strong> After the class has started or less than 24 hours before start time.
                    </p>
                  </div>
                </div>

                <h2 className="text-xl font-semibold text-gray-900">Recorded Lessons (Video Content)</h2>
                <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-800">
                    <strong>No refund:</strong> Once access to the content has been granted, refunds cannot be made.
                  </p>
                </div>

                <h2 className="text-xl font-semibold text-gray-900">Premium Subscriptions</h2>
                <p className="leading-relaxed">
                  Premium subscription refunds are processed on a pro-rata basis if requested within 7 days of purchase and before any premium content has been accessed.
                </p>

                <h2 className="text-xl font-semibold text-gray-900">How to Request a Refund</h2>
                <p className="leading-relaxed">
                  For refund requests, please contact us at <a href="mailto:info@pilatika.com" className="text-purple-600 hover:underline">info@pilatika.com</a> with your order details.
                </p>
              </div>
            ) : (
              <div className="space-y-6 text-gray-700">
                <div className="bg-blue-50 p-4 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                  <p className="text-blue-800">
                    Pilatika üzerinden sunulan hizmetler dijital içerik ve canlı ders hizmeti kapsamındadır.
                  </p>
                </div>

                <h2 className="text-xl font-semibold text-gray-900">Canlı Dersler</h2>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-green-800">
                      <strong>İade yapılabilir:</strong> Ders başlangıç saatine 24 saatten fazla süre kala yapılan iptallerde iade talep edilebilir.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800">
                      <strong>İade yapılamaz:</strong> Ders başladıktan sonra veya başlangıca 24 saatten az süre kaldığında.
                    </p>
                  </div>
                </div>

                <h2 className="text-xl font-semibold text-gray-900">Kayıtlı Dersler (Video İçerik)</h2>
                <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-800">
                    <strong>İade yapılamaz:</strong> Kayıtlı (izlenebilir) ders içeriklerinde, içeriğe erişim sağlandıktan sonra iade yapılmaz.
                  </p>
                </div>

                <h2 className="text-xl font-semibold text-gray-900">Premium Üyelikler</h2>
                <p className="leading-relaxed">
                  Premium üyelik iadeleri, satın alma tarihinden itibaren 7 gün içinde ve henüz premium içeriğe erişim sağlanmamışsa oransal olarak işlenir.
                </p>

                <h2 className="text-xl font-semibold text-gray-900">İade Talebi Nasıl Yapılır?</h2>
                <p className="leading-relaxed">
                  İade talepleri için sipariş bilgilerinizle birlikte <a href="mailto:info@pilatika.com" className="text-purple-600 hover:underline">info@pilatika.com</a> adresinden bize ulaşabilirsiniz.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RefundPage;
